import React, { useEffect, useState } from "react";
import { getStorage } from "../utils/storage";
import { useNavigate } from "react-router-dom";
import { Page, ProgressCircular, Button } from "react-onsenui";
import { axiosInstance as axios } from "../utils/axios";
import { paths } from "../App";
import { format, parseISO, isToday, isTomorrow } from "date-fns";
import {
    Card,
    CardContent,
    Collapse,
    Typography,
    Chip,
    List,
    ListItem,
    ListItemText,
    Divider,
} from "@mui/material";
import { Icon } from "@iconify/react";
import { clPageStyle, CL_PRIMARY, ClPageHeader, ClPageTitle } from "./cl-styles";

/** Safely parse any date string — returns a Date or null */
const safeParse = (dt) => {
    if (!dt) return null;
    try {
        const d = parseISO(String(dt));
        return isNaN(d.getTime()) ? new Date(dt) : d;
    } catch { return null; }
};

/** Format a date string as "hh:mm AM/PM" */
const fmtTime = (dt) => { const d = safeParse(dt); return d ? format(d, "hh:mm a") : "—"; };

/** Format a date string as "d MMM" (e.g. "2 Oct") */
const fmtDate = (dt) => { const d = safeParse(dt); return d ? format(d, "d MMM") : "—"; };

/**
 * Return a day label from the scheduled_for string relative to today.
 * "Today", "Tomorrow", or "3 Oct"
 */
const getDayLabel = (scheduledFor) => {
    const d = safeParse(scheduledFor);
    if (!d) return "—";
    if (isToday(d)) return "Today";
    if (isTomorrow(d)) return "Tomorrow";
    return fmtDate(scheduledFor);
};

export const ChecklistUpcoming = () => {
    const [authUser] = useState(getStorage("u4"));
    const navigate = useNavigate();
    const goBack = () => navigate(paths.checklist.dash, { replace: true });

    // ── Pagination state (mirrors MyInstances pattern) ──────────────────────
    const [pageNum, setPageNum] = useState(1);
    const [pageSize] = useState(10);
    const [items, setItems] = useState(null); // null = initial load
    const [hasMore, setHasMore] = useState(false);
    const [loading, setLoading] = useState(false);
    const [fetchError, setFetchError] = useState(false);

    // ── Expand/collapse per card ─────────────────────────────────────────────
    // Key: `${st_id}-${scheduled_for}`, value: boolean
    const [expanded, setExpanded] = useState({});

    const toggleExpand = (key) =>
        setExpanded((prev) => ({ ...prev, [key]: !prev[key] }));

    // ── Fetch ─────────────────────────────────────────────────────────────────
    const fetchUpcoming = async () => {
        if (!authUser?.id) return;
        setLoading(true);
        setFetchError(false);
        try {
            // POST by design: pagination params go in the request body (consistent with other CL list endpoints e.g. /checklist/my/instances).
            // Backend: ChecklistsStaffController@upcoming — returns { data: ChecklistSchedule[], has_more: bool }.
            // If the shape changes, incoming falls back to [] and the empty state renders safely.
            const r = await axios.post("/checklist/my/upcoming", {
                page: pageNum,
                per_page: pageSize,
            });
            const incoming = Array.isArray(r?.data?.data) ? r.data.data : [];
            // Dedup by composite key (st_id + scheduled_for) to guard against duplicate pages on re-fetch
            setItems((prev) => {
                const merged = prev ? prev.concat(incoming) : incoming;
                const seen = new Set();
                return merged.filter(item => {
                    const key = `${item.st_id}-${item.scheduled_for}`;
                    if (seen.has(key)) return false;
                    seen.add(key);
                    return true;
                });
            });
            setHasMore(r?.data?.has_more ?? false);
        } catch (err) {
            console.error("Upcoming checklists fetch error:", err);
            setFetchError(true);
            if (items === null) setItems([]); // don't leave spinner forever
        } finally {
            setLoading(false);
        }
    };

    useEffect(() => {
        fetchUpcoming();
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [pageNum]);

    // ── Render helpers ────────────────────────────────────────────────────────

    /**
     * Insert day-header dividers into the flat items list.
     * Returns an array of either { type: 'header', label } or { type: 'item', data }.
     */
    const buildRows = (itemList) => {
        const rows = [];
        let lastLabel = null;
        itemList.forEach((item) => {
            const label = getDayLabel(item.scheduled_for);
            if (label !== lastLabel) {
                rows.push({ type: "header", label });
                lastLabel = label;
            }
            rows.push({ type: "item", data: item });
        });
        return rows;
    };

    const renderCard = (item, index) => {
        const cardKey = `${item.st_id}-${item.scheduled_for}`;
        const isOpen = !!expanded[cardKey];
        const locCount = item.locations?.length ?? 0;

        return (
            <Card
                key={`${cardKey}-${index}`}
                variant="outlined"
                sx={{ mb: 1.5, borderRadius: 2, cursor: "default" }}
            >
                <CardContent
                    sx={{ pb: "12px !important", pt: 1.5, px: 2 }}
                    onClick={() => locCount > 0 && toggleExpand(cardKey)}
                    style={{ cursor: locCount > 0 ? "pointer" : "default" }}
                >
                    {/* Top row: time chip + template name */}
                    <div
                        style={{
                            display: "flex",
                            alignItems: "center",
                            justifyContent: "space-between",
                            gap: 8,
                        }}
                    >
                        <div style={{ display: "flex", alignItems: "center", gap: 8, flex: 1, minWidth: 0 }}>
                            <Chip
                                icon={<Icon icon="mdi:clock-outline" style={{ fontSize: 14 }} />}
                                label={fmtTime(item.scheduled_for)}
                                size="small"
                                color="primary"
                                variant="outlined"
                                sx={{ fontWeight: 600, fontSize: "0.78rem", flexShrink: 0 }}
                            />
                            <Typography
                                variant="subtitle2"
                                sx={{
                                    fontWeight: 600,
                                    overflow: "hidden",
                                    textOverflow: "ellipsis",
                                    whiteSpace: "nowrap",
                                }}
                                title={item.blueprint_name}
                            >
                                {item.blueprint_name}
                            </Typography>
                        </div>

                        {/* Expand toggle */}
                        {locCount > 0 && (
                            <div style={{ display: "flex", alignItems: "center", gap: 4, flexShrink: 0 }}>
                                <Typography
                                    variant="caption"
                                    sx={{ color: "text.secondary", whiteSpace: "nowrap" }}
                                >
                                    <Icon icon="mdi:map-marker-outline" style={{ fontSize: 13, verticalAlign: "middle", marginRight: 2 }} />
                                    {locCount} {locCount === 1 ? "location" : "locations"}
                                </Typography>
                                <Icon
                                    icon={isOpen ? "mdi:chevron-up" : "mdi:chevron-down"}
                                    style={{ fontSize: 18, color: "rgba(0,0,0,0.54)", flexShrink: 0 }}
                                />
                            </div>
                        )}
                    </div>

                    {/* Subtitle: schedule name + desk */}
                    {(item.schedule_name || item.desk_name) && (
                        <Typography
                            variant="caption"
                            sx={{ color: "text.secondary", display: "block", mt: 0.5, lineHeight: 1.4 }}
                        >
                            {item.schedule_name}
                            {item.desk_name && (
                                <span style={{ marginLeft: 6, opacity: 0.7 }}>· {item.desk_name}</span>
                            )}
                        </Typography>
                    )}

                    {/* Collapsible locations list */}
                    {locCount > 0 && (
                        <Collapse in={isOpen} timeout="auto" unmountOnExit>
                            <Divider sx={{ my: 1 }} />
                            <List dense disablePadding>
                                {item.locations.map((loc, li) => (
                                    <ListItem key={li} disableGutters sx={{ py: 0.25 }}>
                                        <Icon
                                            icon="mdi:map-marker-outline"
                                            style={{ fontSize: 14, marginRight: 6, color: "rgba(0,0,0,0.54)", flexShrink: 0 }}
                                        />
                                        <ListItemText
                                            primary={loc.location}
                                            primaryTypographyProps={{
                                                variant: "body2",
                                                sx: { lineHeight: 1.4 },
                                            }}
                                        />
                                    </ListItem>
                                ))}
                            </List>
                        </Collapse>
                    )}
                </CardContent>
            </Card>
        );
    };

    const renderDayHeader = (label) => (
        <div
            key={`hdr-${label}`}
            style={{
                display: "flex",
                alignItems: "center",
                gap: 8,
                margin: "18px 0 8px",
            }}
        >
            <Typography
                variant="overline"
                sx={{
                    fontWeight: 700,
                    color: "text.secondary",
                    lineHeight: 1,
                    letterSpacing: "0.08em",
                }}
            >
                {label}
            </Typography>
            <div style={{ flex: 1, height: 1, backgroundColor: "rgba(0,0,0,0.1)" }} />
        </div>
    );

    const rows = items ? buildRows(items) : [];

    return (
        <Page
            style={clPageStyle}
            renderToolbar={() => <ClPageHeader onBack={goBack} />}
        >
            <div style={{ padding: '12px 16px 80px' }}>
                <ClPageTitle title="Upcoming Checklists" subtitle="Scheduled checklists coming up for your department" />

                {/* Initial loading spinner */}
                {items === null && (
                    <div
                        className="flex flex-both-center"
                        style={{ height: "100%", minHeight: "75vh" }}
                    >
                        <ProgressCircular modifier="material" indeterminate={true} />
                    </div>
                )}

                {/* Error state */}
                {fetchError && (
                    <div className="flex flex-both-center flex-dir-col" style={{ minHeight: "65vh", gap: 12 }}>
                        <Icon icon="mdi:alert-circle-outline" style={{ fontSize: 36, color: '#e53935', opacity: 0.7 }} />
                        <span style={{ opacity: 0.6, textAlign: "center" }}>Failed to load upcoming checklists</span>
                        <Button modifier="quiet" onClick={() => { setItems(null); setFetchError(false); fetchUpcoming(); }}>
                            Retry
                        </Button>
                    </div>
                )}

                {/* Empty state */}
                {!fetchError && items !== null && items.length === 0 && (
                    <div
                        className="flex flex-both-center flex-dir-col"
                        style={{ height: "100%", minHeight: "65vh", gap: 12 }}
                    >
                        <span style={{ opacity: 0.5, textAlign: "center" }}>
                            No upcoming scheduled checklists found
                        </span>
                        <Button
                            modifier="quiet"
                            onClick={() => navigate(paths.checklist.myChecklists)}
                        >
                            Go to My Checklists
                        </Button>
                    </div>
                )}

                {/* List */}
                {items !== null && items.length > 0 && (
                    <div>
                        {rows.map((row, i) =>
                            row.type === "header"
                                ? renderDayHeader(row.label)
                                : renderCard(row.data, i)
                        )}

                        {/* Load More */}
                        <div
                            style={{
                                display: "flex",
                                placeItems: "center",
                                paddingTop: 16,
                                paddingBottom: 24,
                            }}
                        >
                            {loading ? (
                                <ProgressCircular
                                    modifier="material"
                                    indeterminate={true}
                                    style={{ margin: "0 auto" }}
                                />
                            ) : (
                                <Button
                                    style={{ margin: "0 auto", display: "block" }}
                                    onClick={() => setPageNum((p) => p + 1)}
                                    disabled={!hasMore}
                                >
                                    {hasMore ? "Load more" : "All caught up"}
                                </Button>
                            )}
                        </div>
                    </div>
                )}
            </div>
        </Page>
    );
};
